import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import type * as ozon from "@/api/ozonSupply";
import { cn } from "@/lib/utils";
import { pad2 } from "../format";
import { DAY_PARTS, MONTHS, WEEKDAYS, dayLabel } from "./slotFormat";

/** Выбор окна приёмки: календарь слева, интервалы выбранного дня справа — как в кабинете площадки.
 *
 *  Площадка отдаёт под две сотни окон на две недели. Сплошной стеной одинаковых кнопок с полной
 *  датой это не выбор, а поиск глазами: менеджер сначала решает ДЕНЬ, и только потом — час.
 *  Календарь заодно показывает и то, чего список не показывал: в какие дни площадка не принимает
 *  вовсе — они серые и некликабельны.
 */
export function SlotPicker({
  slots,
  busy,
  onPick,
}: {
  slots: ozon.OzonTimeslot[];
  busy: boolean;
  onPick: (slot: ozon.OzonTimeslot) => void;
}): React.ReactElement {
  const byDay = new Map<string, ozon.OzonTimeslot[]>();
  for (const s of slots) {
    const d = s.start.slice(0, 10);
    const same = byDay.get(d);
    if (same) same.push(s);
    else byDay.set(d, [s]);
  }
  const days = [...byDay.keys()].sort();
  const first = days[0] ?? "";
  const [day, setDay] = useState(first);
  const [month, setMonth] = useState(first.slice(0, 7));

  if (slots.length === 0)
    return (
      <div className="border-t px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
        Площадка не дала ни одного окна приёмки на ближайшие две недели.
      </div>
    );

  const year = Number(month.slice(0, 4));
  const mon = Number(month.slice(5, 7));
  const shifted = (delta: number): string => {
    const d = new Date(Date.UTC(year, mon - 1 + delta, 1));
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
  };
  // листаем только там, где окна есть: пустой месяц показывать незачем
  const canPrev = shifted(-1) >= first.slice(0, 7);
  const canNext = shifted(1) <= (days[days.length - 1] ?? first).slice(0, 7);

  const lead = (new Date(Date.UTC(year, mon - 1, 1)).getUTCDay() + 6) % 7; // неделя с понедельника
  const total = new Date(Date.UTC(year, mon, 0)).getUTCDate();
  const cells: (string | null)[] = [
    ...Array<null>(lead).fill(null),
    ...Array.from({ length: total }, (_, i) => `${month}-${pad2(i + 1)}`),
  ];
  const now = new Date();
  const today = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  const hours = byDay.get(day) ?? [];

  return (
    <div className="border-t px-3 py-2 text-xs">
      <div className="mb-2 text-muted-foreground">
        Дата и время отгрузки — в часовом поясе склада, показываем все доступные
        интервалы. Не берите срок, в который склад не успеет собрать: непоставка
        в срок — штраф площадки. Выбор интервала <b>создаёт заявку</b> — она
        видна в кабинете, но отменяется.
      </div>
      <div className="flex flex-wrap gap-6">
        <div className="w-56 shrink-0">
          <div className="mb-1 flex items-center justify-between">
            <button
              type="button"
              disabled={!canPrev}
              onClick={() => setMonth(shifted(-1))}
              className="rounded p-0.5 hover:bg-accent disabled:opacity-25"
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="font-medium">
              {MONTHS[mon - 1]}{" "}
              <span className="text-muted-foreground">{year}</span>
            </span>
            <button
              type="button"
              disabled={!canNext}
              onClick={() => setMonth(shifted(1))}
              className="rounded p-0.5 hover:bg-accent disabled:opacity-25"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 justify-items-center gap-y-0.5">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-[10px] text-muted-foreground">
                {w}
              </div>
            ))}
            {cells.map((c, i) =>
              c === null ? (
                <div key={`pad-${i}`} />
              ) : (
                <button
                  key={c}
                  type="button"
                  disabled={!byDay.has(c)}
                  onClick={() => setDay(c)}
                  title={
                    byDay.has(c)
                      ? `окон: ${byDay.get(c)!.length}`
                      : "площадка не принимает"
                  }
                  className={cn(
                    "relative flex size-7 items-center justify-center rounded-lg tabular-nums",
                    c === day
                      ? "bg-foreground font-medium text-background"
                      : !byDay.has(c)
                        ? "text-muted-foreground/40"
                        : cn("hover:bg-accent", i % 7 >= 5 && "text-rose-500"),
                    c === today && c !== day && "underline",
                  )}
                >
                  {Number(c.slice(8, 10))}
                  {byDay.has(c) && c !== day && (
                    <span className="absolute bottom-0.5 size-1 rounded-full bg-sky-500" />
                  )}
                </button>
              ),
            )}
          </div>
        </div>

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="font-medium">
            {dayLabel(day)}{" "}
            <span className="font-normal text-muted-foreground">
              · окон {hours.length}
            </span>
          </div>
          {DAY_PARTS.map((part) => {
            const inPart = hours.filter((h) => {
              const hh = Number(h.start.slice(11, 13));
              return hh >= part.from && hh < part.to;
            });
            if (inPart.length === 0) return null;
            return (
              <div key={part.title}>
                <div className="text-muted-foreground">{part.title}</div>
                <div className="mt-0.5 flex flex-wrap gap-1">
                  {inPart.map((h) => (
                    <button
                      key={h.start}
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Создать заявку на ${dayLabel(day)}, ${h.start.slice(11, 16)}–${h.end.slice(11, 16)}? Она появится в кабинете Ozon.`,
                          )
                        )
                          onPick(h);
                      }}
                      className="rounded-lg border px-2 py-1 tabular-nums hover:bg-accent disabled:opacity-50"
                    >
                      {h.start.slice(11, 16)} – {h.end.slice(11, 16)}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
